import { cn } from '../utils/cn';

export type CornerLabel = 'TL' | 'TR' | 'BR' | 'BL' | 'T' | 'B';

export type Point = { x: number; y: number };

export function Handle({
  label,
  position,
  selected = false,
  dimmed = false,
  showCoord = false,
  onSelect,
  bindProps,
}: {
  label: CornerLabel;
  /** Normalized 0..1 coords inside the parent wall-canvas, Y-down */
  position: Point;
  selected?: boolean;
  /** Mirror-mode preview: the other wall's handle is shown but non-interactive */
  dimmed?: boolean;
  showCoord?: boolean;
  onSelect?: () => void;
  /** Spread of @use-gesture/react bind() — attaches pointer handlers */
  bindProps?: Record<string, unknown>;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      {...bindProps}
      aria-label={`${label} corner at ${position.x.toFixed(3)}, ${position.y.toFixed(3)}`}
      aria-pressed={selected}
      style={{
        left: `${position.x * 100}%`,
        top: `${position.y * 100}%`,
      }}
      className={cn(
        'absolute w-7 h-7 -ml-3.5 -mt-3.5 bg-transparent border-none p-0',
        'cursor-grab active:cursor-grabbing touch-none',
        selected ? 'z-20' : 'z-10',
        dimmed && 'opacity-40 pointer-events-none',
      )}
    >
      {/* Outer ring */}
      <span
        className={cn(
          'absolute inset-0 rounded-full transition-all duration-fast ease-snap',
          'bg-bg/70',
          selected
            ? 'border-2 border-accent shadow-accent-glow bg-accent/5'
            : 'border-[1.5px] border-text-dim group-hover:border-text',
        )}
      />
      {/* Horizontal cross arm */}
      <span
        className={cn(
          'absolute left-1.5 right-1.5 top-1/2 transition-colors duration-fast ease-snap',
          selected ? 'bg-accent h-[2px] -mt-[0.75px]' : 'bg-text-dim h-px -mt-[0.5px]',
        )}
      />
      {/* Vertical cross arm */}
      <span
        className={cn(
          'absolute top-1.5 bottom-1.5 left-1/2 transition-colors duration-fast ease-snap',
          selected ? 'bg-accent w-[2px] -ml-[0.75px]' : 'bg-text-dim w-px -ml-[0.5px]',
        )}
      />
      {/* Label above */}
      <span
        className={cn(
          'absolute -top-[22px] left-1/2 -translate-x-1/2 px-1.5 py-[1px] rounded-sm',
          'font-mono text-[11px] font-semibold uppercase tracking-[0.1em] pointer-events-none whitespace-nowrap',
          'transition-all duration-fast ease-snap',
          selected
            ? 'text-accent border border-accent-ring bg-[rgba(26,15,0,0.9)]'
            : 'text-text-dimmer bg-panel',
        )}
      >
        {label}
      </span>
      {/* Coord readout — only when selected */}
      {selected && showCoord && (
        <span
          className={cn(
            'absolute top-[38px] left-1/2 -translate-x-1/2 px-1.5 py-[4px] rounded-sm',
            'font-mono text-[12px] text-accent bg-[rgba(10,11,13,0.95)] border border-accent-ring',
            'whitespace-nowrap pointer-events-none',
          )}
        >
          {position.x.toFixed(3)}, {position.y.toFixed(3)}
        </span>
      )}
    </button>
  );
}
